/**
 * The arithmetic behind `useScreen` (lib/screens.ts) and the page-load measures (lib/visit.ts), with
 * no DOM and no React so it can be tested: how an element's id becomes something safe to send, what
 * counts as a rage click, how far down a page she got, the running totals of one screen visit, and
 * how layout shifts add up.
 */

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi
/** What the api accepts (events/kinds.ts `staticId`): words, digits, `_`, `-`, and `:id` where a record id stood. */
const STATIC_ID = /^(?:[a-z0-9_-]|:id)+$/i
/** Ids that React and Radix make up per render: they name nothing and change on every mount. */
const GENERATED_ID = /^(?:_r_[0-9a-z]+_|radix-)/i
const MAX_ID = 32

/**
 * The id of a control, safe to send: record ids become `:id`, so `ex-<uuid>-revise` and every other
 * exercise's revise button are one control. Null for anything that is not a design name (an id
 * with spaces or other characters, a generated one, or one too long to mean anything).
 */
export function maskId(id: string): string | null {
	const masked = id.replace(UUID, ':id')
	if (masked.length === 0 || masked.length > MAX_ID) return null
	if (!STATIC_ID.test(masked) || GENERATED_ID.test(masked)) return null
	return masked
}

/** Three presses on the same thing inside 1.2 seconds: she is pressing something that is not answering. */
export const RAGE_CLICKS = 3
export const RAGE_WINDOW_MS = 1200

/**
 * Reports a burst once: the press that makes the third inside the window says yes, then the count
 * starts over, so ten angry presses are three bursts at most, never ten.
 */
export function createRageDetector() {
	let target: unknown
	let times: number[] = []
	return {
		press(on: unknown, at: number): boolean {
			if (on !== target) {
				target = on
				times = []
			}
			times = times.filter((t) => at - t <= RAGE_WINDOW_MS)
			times.push(at)
			if (times.length < RAGE_CLICKS) return false
			times = []
			return true
		},
	}
}

export type ScrollDepth = 0 | 25 | 50 | 75 | 100

/** How much of the page has been on screen, in quarters (its bottom edge so far); a page that does not scroll is all seen. */
export function scrollDepth(
	scrollTop: number,
	viewHeight: number,
	pageHeight: number,
): ScrollDepth {
	if (pageHeight <= viewHeight + 1) return 100
	// Within a few pixels of the end counts as the end: browsers round, bars hide a line.
	const seen = Math.min(1, (scrollTop + viewHeight + 4) / pageHeight)
	if (seen >= 1) return 100
	if (seen >= 0.75) return 75
	if (seen >= 0.5) return 50
	if (seen >= 0.25) return 25
	return 0
}

const MAX_MS = 3_600_000
const MAX_COUNT = 10_000
const MAX_CLICKED = 6
const cap = (n: number, max: number) => Math.max(0, Math.min(Math.round(n), max))

export type ScreenReport = {
	ms: number
	scroll: ScrollDepth
	clicks: number
	rage: number
	clicked: string[]
	seen: number
	items: number
}

/**
 * The running totals of one visit to one screen. Time, presses and rage are counted since the last
 * `take`; how far she scrolled and which items came into view are the screen's total, because a
 * screen she returns to keeps what she already saw. Time counts only while the page is visible.
 */
export function createScreenStats(now: () => number = Date.now, visible = true) {
	let since: number | null = visible ? now() : null
	let ms = 0
	let clicks = 0
	let rage = 0
	let clicked: string[] = []
	let depth: ScrollDepth = 0
	let known = 0
	const seen = new Set<unknown>()
	const rager = createRageDetector()

	const settle = () => {
		if (since === null) return
		const t = now()
		ms += t - since
		since = t
	}

	return {
		show: () => {
			since ??= now()
		},
		hide: () => {
			settle()
			since = null
		},
		/** `id` is the pressed control's masked id, or null when it has none worth sending. */
		press: (on: unknown, id: string | null) => {
			clicks += 1
			if (rager.press(on, now())) rage += 1
			if (id && clicked.length < MAX_CLICKED && !clicked.includes(id)) clicked.push(id)
		},
		scrolled: (d: ScrollDepth) => {
			if (d > depth) depth = d
		},
		saw: (item: unknown) => {
			seen.add(item)
		},
		/** How many items the screen holds, when the page knows. */
		items: (n: number) => {
			known = Math.max(known, n)
		},
		/** Whether anything happened since the last `take`: a screen that was never really on does not report. */
		active: () => {
			settle()
			return ms > 0 || clicks > 0
		},
		take: (): ScreenReport => {
			settle()
			const report: ScreenReport = {
				ms: cap(ms, MAX_MS),
				scroll: depth,
				clicks: cap(clicks, MAX_COUNT),
				rage: cap(rage, MAX_COUNT),
				clicked,
				seen: cap(seen.size, MAX_COUNT),
				items: cap(Math.max(known, seen.size), MAX_COUNT),
			}
			ms = 0
			clicks = 0
			rage = 0
			clicked = []
			return report
		},
	}
}

/** The shifts that count are the worst burst: ones less than a second apart, in a window of at most five. */
const SHIFT_GAP_MS = 1000
const SHIFT_WINDOW_MS = 5000

/**
 * Cumulative layout shift as browsers define it: shifts that follow no input are grouped into
 * session windows, and the score is the worst window, not the sum over a long visit.
 */
/** What `nameOfShifted` needs of an element: so the arithmetic stays testable without a DOM. */
export type ShiftedElement = {
	tagName: string
	closest: (selector: string) => { id: string } | null
}

/**
 * What moved in a layout shift, as something safe to send: the id of the element or of its nearest
 * ancestor that has one (masked like a pressed control's), else its tag. A design name, never text.
 */
export function nameOfShifted(el: ShiftedElement | null): string | undefined {
	if (!el) return undefined
	const id = el.closest('[id]')?.id
	const named = id ? maskId(id) : null
	if (named) return named
	const tag = el.tagName.toLowerCase()
	return STATIC_ID.test(tag) && tag.length <= MAX_ID ? tag : undefined
}

export function createLayoutShift() {
	let windowValue = 0
	let windowStart = 0
	let last = 0
	let worst = 0
	return {
		add(value: number, at: number): void {
			if (windowValue > 0 && (at - last > SHIFT_GAP_MS || at - windowStart > SHIFT_WINDOW_MS)) {
				windowValue = 0
			}
			if (windowValue === 0) windowStart = at
			windowValue += value
			last = at
			worst = Math.max(worst, windowValue)
		},
		/** Times 1000, as an integer, capped at what the api takes. */
		score: (): number => Math.min(Math.round(worst * 1000), 10_000),
	}
}
